import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useMemo } from "react";
import { Toaster } from "sonner";

import { ThemeProvider } from "@/lib/theme";
import { AuthProvider } from "@/lib/auth";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { ProtectedRoute } from "@/components/app/AppShell";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Pricing from "@/pages/Pricing";
import FAQ from "@/pages/FAQ";
import Contact from "@/pages/Contact";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Refund from "@/pages/Refund";
import PhotoContentPolicy from "@/pages/PhotoContentPolicy";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import SessionIdHandler from "@/components/auth/SessionIdHandler";
import Passport from "@/pages/Passport";
import PassportStamps from "@/pages/PassportStamps";
import Explore from "@/pages/Explore";
import OwnerHome from "@/pages/OwnerHome";
import CampsiteDetail from "@/pages/CampsiteDetail";
import CheckinPreview from "@/pages/CheckinPreview";
import Achievements from "@/pages/Achievements";
import Challenges from "@/pages/Challenges";
import Notifications from "@/pages/Notifications";
import AdminHome from "@/pages/AdminHome";
import AdminCampsites from "@/pages/AdminCampsites";
import AdminUsers from "@/pages/AdminUsers";
import AdminClaims from "@/pages/AdminClaims";
import StaffInvite from "@/pages/StaffInvite";
import Leaderboards from "@/pages/Leaderboards";
import OwnerAnalytics from "@/pages/OwnerAnalytics";
import OwnerEvents from "@/pages/OwnerEvents";
import OwnerEventEditor from "@/pages/OwnerEventEditor";
import Profile from "@/pages/Profile";
import EditProfile from "@/pages/EditProfile";

// Routes rendered WITHOUT the marketing header/footer (they either provide
// their own chrome via AppShell or are full-bleed auth screens).
const CHROMELESS_PREFIXES = [
  "/login", "/register", "/forgot-password", "/reset-password",
  "/passport", "/explore", "/owner", "/campsites",
  "/checkin", "/achievements", "/challenges", "/notifications",
  "/admin", "/staff", "/leaderboards",
  "/profile",
];

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

function ChromeWrapper({ children }) {
  const { pathname } = useLocation();
  // Hide the marketing header + footer ONLY on internal dashboard / profile
  // routes and full-bleed auth screens (list above). Public marketing pages
  // — Home, About, Pricing, FAQ, Contact, legal, payment success/cancel —
  // ALWAYS show the marketing header, whether the visitor is signed in or
  // not, so the top nav stays reachable from any landing/marketing URL.
  const chromeless = useMemo(
    () => CHROMELESS_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/")),
    [pathname],
  );
  if (chromeless) return children;
  return (
    <>
      <Header />
      <main>{children}</main>
      <Footer />
    </>
  );
}

function AnimatedRoutes() {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        <Routes location={location}>
          {/* Public marketing */}
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/refund" element={<Refund />} />
          <Route path="/photo-content-policy" element={<PhotoContentPolicy />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />

          {/* Auth */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Camper */}
          <Route path="/passport" element={<ProtectedRoute><Passport /></ProtectedRoute>} />
          <Route path="/passport/stamps" element={<ProtectedRoute><PassportStamps /></ProtectedRoute>} />
          <Route path="/explore" element={<ProtectedRoute><Explore /></ProtectedRoute>} />
          <Route path="/campsites/:id" element={<ProtectedRoute><CampsiteDetail /></ProtectedRoute>} />
          <Route path="/achievements" element={<ProtectedRoute><Achievements /></ProtectedRoute>} />
          <Route path="/challenges" element={<ProtectedRoute><Challenges /></ProtectedRoute>} />
          <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
          <Route path="/leaderboards" element={<ProtectedRoute><Leaderboards /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/profile/edit" element={<ProtectedRoute><EditProfile /></ProtectedRoute>} />

          {/* Owner */}
          <Route path="/owner" element={<ProtectedRoute ownerOnly><OwnerHome /></ProtectedRoute>} />
          <Route path="/owner/analytics" element={<ProtectedRoute ownerOnly><OwnerAnalytics /></ProtectedRoute>} />
          <Route path="/owner/events" element={<ProtectedRoute ownerOnly><OwnerEvents /></ProtectedRoute>} />
          <Route path="/owner/events/new" element={<ProtectedRoute ownerOnly><OwnerEventEditor /></ProtectedRoute>} />
          <Route path="/owner/events/:eventId" element={<ProtectedRoute ownerOnly><OwnerEventEditor /></ProtectedRoute>} />
          <Route path="/checkin/preview/:token" element={<ProtectedRoute><CheckinPreview /></ProtectedRoute>} />

          {/* Admin */}
          <Route path="/admin" element={<ProtectedRoute adminOnly><AdminHome /></ProtectedRoute>} />
          <Route path="/admin/campsites" element={<ProtectedRoute adminOnly><AdminCampsites /></ProtectedRoute>} />
          <Route path="/admin/users" element={<ProtectedRoute adminOnly><AdminUsers /></ProtectedRoute>} />
          <Route path="/admin/claims" element={<ProtectedRoute adminOnly><AdminClaims /></ProtectedRoute>} />

          {/* Staff invitations */}
          <Route path="/staff/invites/:code" element={<StaffInvite />} />

          <Route path="*" element={<Home />} />
        </Routes>
      </motion.div>
    </AnimatePresence>
  );
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <ScrollToTop />
            <SessionIdHandler />
            <ChromeWrapper>
              <AnimatedRoutes />
            </ChromeWrapper>
            <Toaster
              position="bottom-right"
              toastOptions={{
                style: {
                  borderRadius: 12,
                  border: "1px solid var(--border-c)",
                  background: "var(--card)",
                  color: "var(--on-surface)",
                },
              }}
            />
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
